import type { PlanTemplateStep } from '@/@studio-ai/types';

/**
 * Renders a committed answer for display.
 *
 * Two rules, both learned the hard way:
 *
 *  1. `select` / `multi_select` answers are stored as option **ids** (that is
 *     what the INPUTS block must carry), so the transcript has to map them back
 *     to the option's authored label. Showing the id — humanized or not — makes
 *     the user's own answer look like something they didn't say.
 *  2. Free text is never reformatted. `humanizeLabel` title-cases words and
 *     turns hyphens into spaces, which quietly rewrites "Coca-Cola" as
 *     "Coca Cola" and "Liquid IV" as "Liquid Iv". The stored value is fine, but
 *     a review screen that misreports what will be sent is worse than no
 *     review screen.
 */
export function formatAnswer(step: PlanTemplateStep | undefined, value: string): string {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return '';

    const options = step?.options;
    if (!options?.length) return trimmed;

    const labelById = new Map(options.map((option) => [option.id, option.label]));
    return (
        trimmed
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean)
            // An unmatched entry is a custom "Other…" answer — the user's own words.
            .map((entry) => labelById.get(entry) ?? entry)
            .join(', ')
    );
}
